import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Seo, withTemplate, canonicalize } from '@/lib/seo';
import { breadcrumbJsonLd } from '@/lib/structuredData';
import JsonLD from '@/components/JsonLD';
import { fireEvent } from '@/lib/analytics';
import { toast } from '@/hooks/use-toast';

interface OperateFormData {
  name: string;
  email: string;
  company: string;
  useCase: string;
  plan: string;
  timeCommitment: string;
}

export default function Operate() {
  const [formData, setFormData] = useState<OperateFormData>({
    name: '',
    email: '',
    company: '',
    useCase: '',
    plan: '',
    timeCommitment: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim() || !formData.email.trim() || !formData.useCase.trim()) {
      toast({
        title: 'Please fill in all required fields',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Analytics: Track operate application
      fireEvent('operate_applied', {
        has_company: !!formData.company.trim(),
        use_case_length: formData.useCase.length,
        has_plan: !!formData.plan.trim(),
      });

      // Simulate API call (replace with actual submission)
      await new Promise((resolve) => setTimeout(resolve, 1000));

      setSubmitted(true);
      toast({
        title: 'Application submitted successfully',
        description: "I'll review your application and get back to you soon.",
      });
    } catch (error) {
      toast({
        title: 'Failed to submit application',
        description: 'Please try again or contact me directly.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof OperateFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  if (submitted) {
    return (
      <>
        <Seo
          title={withTemplate('Application Submitted - Operate (80/20)')}
          description="Your 80/20 operate application has been submitted successfully."
          canonical={canonicalize('/operate')}
        />

        <div className="min-h-screen bg-gradient-to-br from-background to-muted/20 py-12 px-4">
          <div className="max-w-2xl mx-auto text-center">
            <Card className="apple-card">
              <CardContent className="py-12">
                <h1 className="text-3xl font-bold text-foreground mb-4">Application Submitted</h1>
                <p className="text-lg text-muted-foreground mb-8">
                  Thank you for your interest in our 80/20 operate program. I'll review your
                  application and get back to you within 2 business days.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button asChild size="lg">
                    <Link to="/domains">Browse Domains</Link>
                  </Button>
                  <Button asChild variant="outline" size="lg">
                    <Link to="/contact">Contact Me</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Seo
        title={withTemplate('Apply to Operate (80/20)')}
        description="Apply to operate premium domains with our 80/20 partnership program."
        canonical={canonicalize('/operate')}
        og={{
          title: 'Apply to Operate (80/20)',
          description: 'Partner with us to operate premium domains.',
          url: canonicalize('/operate'),
        }}
        twitter={{
          card: 'summary',
          title: 'Apply to Operate (80/20)',
          description: 'Partner with us to operate premium domains.',
        }}
      />

      <JsonLD
        data={breadcrumbJsonLd([
          { name: 'Home', url: canonicalize('/') },
          { name: 'Operate', url: canonicalize('/operate') },
        ])}
      />

      <div className="min-h-screen bg-gradient-to-br from-background to-muted/20 py-12 px-4">
        <div className="max-w-2xl mx-auto space-y-8">
          {/* Header */}
          <div className="text-center space-y-4">
            <h1 className="text-4xl lg:text-5xl font-bold text-foreground">
              Apply to Operate (80/20)
            </h1>
            <p className="text-lg text-muted-foreground">
              Partner with us to operate premium domains. You handle the business development, we
              provide the domain and infrastructure.
            </p>
          </div>

          {/* Program Info */}
          <Card className="apple-card">
            <CardHeader>
              <CardTitle className="text-2xl font-semibold">How the 80/20 Program Works</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="font-semibold text-foreground mb-2">Revenue Sharing</h3>
                <p className="text-sm text-muted-foreground">
                  You receive 80% of net revenue generated from the domain operations. We handle
                  domain costs, infrastructure, and legal compliance.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-foreground mb-2">Your Responsibilities</h3>
                <p className="text-sm text-muted-foreground">
                  Business development, content creation, marketing, customer acquisition, and
                  day-to-day operations of the domain-based business.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-foreground mb-2">Our Support</h3>
                <p className="text-sm text-muted-foreground">
                  Domain ownership, DNS management, hosting infrastructure, legal framework, and
                  business guidance from experienced domain operators.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Application Form */}
          <Card className="apple-card">
            <CardHeader>
              <CardTitle className="text-2xl font-semibold">Application Form</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name" className="text-sm font-medium text-foreground">
                      Full Name *
                    </Label>
                    <Input
                      id="name"
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      placeholder="Your full name"
                      required
                      className="mt-1"
                      maxLength={100}
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-sm font-medium text-foreground">
                      Email Address *
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="your@email.com"
                      required
                      className="mt-1"
                      maxLength={255}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="company" className="text-sm font-medium text-foreground">
                    Company (Optional)
                  </Label>
                  <Input
                    id="company"
                    type="text"
                    value={formData.company}
                    onChange={(e) => handleInputChange('company', e.target.value)}
                    placeholder="Your company name"
                    className="mt-1"
                    maxLength={100}
                  />
                </div>

                <div>
                  <Label htmlFor="useCase" className="text-sm font-medium text-foreground">
                    Use Case & Business Plan *
                  </Label>
                  <Textarea
                    id="useCase"
                    value={formData.useCase}
                    onChange={(e) => handleInputChange('useCase', e.target.value)}
                    placeholder="Describe your business idea for the domain, target market, revenue model, and expected timeline..."
                    required
                    className="mt-1"
                    rows={5}
                    maxLength={2000}
                  />
                </div>

                <div>
                  <Label htmlFor="plan" className="text-sm font-medium text-foreground">
                    Marketing & Growth Plan
                  </Label>
                  <Textarea
                    id="plan"
                    value={formData.plan}
                    onChange={(e) => handleInputChange('plan', e.target.value)}
                    placeholder="How will you drive traffic and generate revenue? What's your marketing strategy?"
                    className="mt-1"
                    rows={4}
                    maxLength={1500}
                  />
                </div>

                <div>
                  <Label htmlFor="timeCommitment" className="text-sm font-medium text-foreground">
                    Time Commitment
                  </Label>
                  <Input
                    id="timeCommitment"
                    type="text"
                    value={formData.timeCommitment}
                    onChange={(e) => handleInputChange('timeCommitment', e.target.value)}
                    placeholder="e.g., 20 hours/week, full-time, part-time"
                    className="mt-1"
                    maxLength={100}
                  />
                </div>

                <div className="bg-muted/30 rounded-xl p-4">
                  <p className="text-sm text-muted-foreground">
                    <span className="font-medium">Next Steps:</span> I'll review your application
                    within 2 business days. Qualified candidates will be invited to a brief
                    interview to discuss partnership details.
                  </p>
                </div>

                <Button
                  type="submit"
                  size="lg"
                  className="w-full"
                  disabled={
                    isSubmitting ||
                    !formData.name.trim() ||
                    !formData.email.trim() ||
                    !formData.useCase.trim()
                  }
                >
                  {isSubmitting ? 'Submitting Application...' : 'Submit Application'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}
